import React, { useEffect, useRef } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement,
  LineController,
  DoughnutController,
  ArcElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import './Charts.css';
import API from '../api';
import { formatCurrency } from '../api/currencyApi';

// Register all ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  DoughnutController,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Charts = ({ currentPeriod, currency }) => {
  const incomeCanvasRef = useRef(null);
  const subscriptionCanvasRef = useRef(null);
  const incomeChartRef = useRef(null);
  const subscriptionChartRef = useRef(null);

  useEffect(() => {
    const loadCharts = async () => {
      try {
        // Destroy existing charts before creating new ones
        if (incomeChartRef.current) {
          incomeChartRef.current.destroy();
          incomeChartRef.current = null;
        }
        if (subscriptionChartRef.current) {
          subscriptionChartRef.current.destroy();
          subscriptionChartRef.current = null;
        }

        const [incomeData, subscriptionData] = await Promise.all([
          API.stats.getIncomeChart(currentPeriod),
          API.stats.getSubscriptionDistribution()
        ]);

        if (incomeCanvasRef.current && incomeData.success) {
          const ctx = incomeCanvasRef.current.getContext('2d');
          incomeChartRef.current = new ChartJS(ctx, {
            type: 'line',
            data: {
              labels: incomeData.data.labels,
              datasets: [{
                label: 'Revenue',
                data: incomeData.data.values,
                borderColor: '#0b1117',
                backgroundColor: 'rgba(11, 17, 23, 0.05)',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointBackgroundColor: '#0b1117',
                pointBorderColor: 'white',
                pointBorderWidth: 2,
                pointHoverRadius: 6
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: true,
              interaction: {
                intersect: false,
                mode: 'index'
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  callbacks: {
                    label: function(context) {
                      return formatCurrency(context.parsed.y, currency);
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: { color: '#f0f2f5' },
                  ticks: {
                    callback: function(value) { 
                      return formatCurrency(value, currency);
                    },
                    font: { size: 12 },
                    color: '#656d76'
                  }
                },
                x: {
                  grid: { display: false },
                  ticks: {
                    font: { size: 12 },
                    color: '#656d76'
                  }
                }
              }
            }
          });
        }

        if (subscriptionCanvasRef.current && subscriptionData.success) {
          const ctx = subscriptionCanvasRef.current.getContext('2d');
          subscriptionChartRef.current = new ChartJS(ctx, {
            type: 'doughnut',
            data: {
              labels: subscriptionData.data.labels,
              datasets: [{
                data: subscriptionData.data.values,
                backgroundColor: ['#0b1117', '#656d76', '#d0d7de'],
                borderColor: 'white',
                borderWidth: 2,
                hoverOffset: 4
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    font: { size: 12 },
                    color: '#656d76',
                    padding: 16,
                    usePointStyle: true,
                    pointStyle: 'circle'
                  }
                },
                tooltip: {
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  padding: 12,
                  titleFont: { size: 12 },
                  bodyFont: { size: 12 },
                  callbacks: {
                    label: function(context) {
                      return 'Users: ' + context.parsed;
                    },
                    afterLabel: function(context) {
                      const total = context.dataset.data.reduce((a, b) => a + b, 0);
                      const percentage = Math.round((context.parsed / total) * 100);
                      return `${percentage}%`;
                    }
                  }
                }
              }
            }
          });
        }
      } catch (error) {
        console.error('Error loading charts:', error);
      }
    };

    loadCharts();

    return () => {
      // Cleanup on unmount
      if (incomeChartRef.current) {
        incomeChartRef.current.destroy();
        incomeChartRef.current = null;
      }
      if (subscriptionChartRef.current) {
        subscriptionChartRef.current.destroy();
        subscriptionChartRef.current = null;
      }
    };
  }, [currentPeriod, currency]);

  return (
    <div className="charts-section">
      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Revenue Trend</h3>
            <span className="chart-meta">Last 7 days</span>
          </div>
          <canvas ref={incomeCanvasRef}></canvas>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3>Plan Distribution</h3>
            <span className="chart-meta">Active subscriptions</span>
          </div>
          <div style={{ position: 'relative', height: '300px' }}>
            <canvas ref={subscriptionCanvasRef}></canvas>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Charts;
